import { Link } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { getRecentlyViewedIds } from '../lib/recentlyViewed';
import type { LayoutConfig } from '../types';
import { SectionCarousel, SectionLayout, resolveDisplay, imageAspectClass } from '../components/SectionCarousel';
import type { SectionDisplayConfig } from '../components/SectionCarousel';
import type { ProductCardDisplayProps } from '../components/ProductCard';

const API_URL = import.meta.env.VITE_API_URL as string;
import { useCategories, useProducts, useSectionProducts } from '../hooks/useCatalog';
import { useHomepageSections, useFaqs, useContactInfo, useSettings, useReviews } from '../hooks/usePublicContent';
import type { PublicReview } from '../hooks/usePublicContent';
import { Container } from '../components/ui/Container';
import { Eyebrow } from '../components/ui/Eyebrow';
import { EditorialImage } from '../components/ui/EditorialImage';
import { ProductCard } from '../components/ProductCard';
import { CategoryCard } from '../components/CategoryCard';
import { QuickViewModal } from '../components/QuickViewModal';
import { HeroSection } from '../components/HeroSection';
import { BundleCard } from '../components/BundleCard';
import { useBundles } from '../hooks/useBundles';
import type { CatalogProduct, BundleSummary } from '../lib/catalogTypes';

function cardDisplayProps(display: SectionDisplayConfig | undefined): ProductCardDisplayProps {
  const cfg = resolveDisplay(display);
  return {
    imageAspect: imageAspectClass(cfg),
    showWishlist: cfg.show_wishlist,
    showQuickView: cfg.show_quick_view,
    showPrice: cfg.show_price,
    showBadge: cfg.show_badges,
    showAddToCart: cfg.show_add_to_cart,
    showDiscountBadge: cfg.show_badges,
  };
}

function layoutToDisplay(layout: LayoutConfig): SectionDisplayConfig {
  return {
    display_mode: layout.displayMode === 'carousel' ? 'carousel' : 'grid',
    columns_desktop: layout.grid?.columns?.desktop ?? 4,
    columns_tablet: layout.grid?.columns?.tablet ?? 2,
    columns_mobile: layout.grid?.columns?.mobile ?? 2,
    rows: layout.grid?.rows?.desktop ?? 2,
    spacing: 'medium',
    image_ratio: layout.image?.ratio === 'square' ? 'square' : layout.image?.ratio === 'landscape' ? 'landscape' : 'portrait',
    items_limit: typeof layout.itemLimit === 'number' ? layout.itemLimit : 'all',
    carousel_slides_desktop: layout.carousel?.slidesPerView?.desktop ?? 4,
    carousel_slides_tablet: layout.carousel?.slidesPerView?.tablet ?? 2,
    carousel_slides_mobile: layout.carousel?.slidesPerView?.mobile ?? 1,
    carousel_space_between: layout.carousel?.spaceBetween ?? 20,
    carousel_loop: layout.carousel?.loop ?? false,
    carousel_autoplay: layout.carousel?.autoplay ?? false,
    carousel_autoplay_speed: layout.carousel?.autoplaySpeed ?? 5000,
    carousel_navigation: layout.carousel?.navigation ?? true,
    carousel_pagination: layout.carousel?.pagination ?? true,
    show_image: layout.cardComponents?.showImage ?? true,
    show_title: layout.cardComponents?.showName ?? true,
    show_price: true,
    show_badges: true,
    show_wishlist: true,
    show_quick_view: true,
    show_add_to_cart: true,
    show_discount_badge: true,
  };
}

const REVIEWS = [
  {
    name: 'Jordan M.',
    quote: 'The frame quality alone is worth the price — feels like something from a proper gallery, not a poster shop.',
    rating: 5,
  },
  {
    name: 'Priya S.',
    quote: 'Ordered three for our hallway. Packaging was excellent and nothing arrived even slightly bent.',
    rating: 5,
  },
  {
    name: 'Alex T.',
    quote: 'Customer service helped me pick a frame colour over email within the hour. Genuinely impressed.',
    rating: 5,
  },
];

const INSTAGRAM_SEEDS = ['ig-1', 'ig-2', 'ig-3', 'ig-4', 'ig-5', 'ig-6'];

// Default section order shown when no sections are configured in the CMS.
const DEFAULT_SECTION_ORDER = [
  'hero_banner', 'trust_bar', 'best_sellers', 'category_grid',
  'custom_print_banner', 'reviews', 'instagram_feed', 'newsletter_banner',
];

export function HomePage() {
  const { data: settings } = useSettings();
  const { data: categories } = useCategories();
  const { data: products, isLoading: productsLoading } = useProducts();
  const { data: sections, isLoading: sectionsLoading } = useHomepageSections();
  const { data: faqs } = useFaqs();
  const { data: contactInfo } = useContactInfo();
  const [quickViewProduct, setQuickViewProduct] = useState<CatalogProduct | null>(null);

  const displayProducts = products ?? [];
  const displayCategories = categories?.map((c) => ({ id: c.id, name: c.name, slug: c.slug, image: c.image, banner_image: c.banner_image })) ?? [];

  // While the API is in-flight, render nothing (avoids flashing fallback content).
  // Only fall back to DEFAULT_SECTION_ORDER once we know the CMS returned no sections.
  const activeSections = sectionsLoading
    ? []
    : sections && sections.length > 0
      ? [...sections].filter(s => s.is_visible !== false).sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
      : DEFAULT_SECTION_ORDER.map((type, i) => ({ id: i, type, is_visible: true, sort_order: i, config: {} as Record<string, unknown> }));

  const renderSection = (s: { id: number; type: string; title?: string | null; config?: Record<string, unknown> }) => {
    const cfg = s.config ?? {};
    switch (s.type) {
      case 'hero_banner':
        return <HeroSection key={s.id} />;
      case 'trust_bar':
        return <TrustBar key={s.id} items={cfg.items as { title: string; copy: string }[] | undefined} />;
      case 'best_sellers':
        return (
          <BestSellers
            key={s.id}
            products={displayProducts}
            isLoading={productsLoading}
            onQuickView={setQuickViewProduct}
            heading={cfg.heading as string | undefined}
            subheading={cfg.subheading as string | undefined}
            productCount={(cfg.product_count as number | undefined) ?? 8}
            showShopAll={(cfg.show_shop_all as boolean | undefined) !== false}
            display={cfg.display as SectionDisplayConfig | undefined}
          />
        );
      case 'category_grid':
      case 'featured_collections':
        return (
          <Collections
            key={s.id}
            categories={displayCategories}
            heading={cfg.heading as string | undefined}
            subheading={cfg.subheading as string | undefined}
            categoryIds={cfg.category_ids as number[] | undefined}
            layout={cfg.layout as LayoutConfig | undefined}
          />
        );
      case 'custom_print_banner':
        return (
          <CustomPrintBanner
            key={s.id}
            eyebrow={cfg.eyebrow as string | undefined}
            heading={cfg.heading as string | undefined}
            body={cfg.body as string | undefined}
            ctaText={cfg.cta_text as string | undefined}
            ctaLink={cfg.cta_link as string | undefined}
            badgeText={cfg.badge_text as string | undefined}
            badgeVisible={cfg.badge_visible as boolean | undefined}
            image={cfg.image as string | undefined}
          />
        );
      case 'reviews':
        return (
          <Reviews
            key={s.id}
            heading={cfg.heading as string | undefined}
            eyebrow={cfg.subheading as string | undefined}
            limit={(cfg.limit as number | undefined) ?? 12}
            autoplaySpeed={(cfg.autoplay_speed as number | undefined) ?? 5000}
          />
        );
      case 'instagram_feed':
        return (
          <Instagram
            key={s.id}
            handle={cfg.handle as string | undefined}
            heading={cfg.heading as string | undefined}
            images={cfg.images as string[] | undefined}
          />
        );
      case 'newsletter_banner':
        return (
          <NewsletterBanner
            key={s.id}
            heading={cfg.heading as string | undefined}
            body={cfg.body as string | undefined}
            buttonText={cfg.button_text as string | undefined}
          />
        );
      case 'about_banner':
        return (
          <AboutBanner
            key={s.id}
            eyebrow={cfg.eyebrow as string | undefined}
            heading={cfg.heading as string | undefined}
            body={cfg.body as string | undefined}
            ctaText={cfg.cta_text as string | undefined}
            ctaLink={cfg.cta_link as string | undefined}
            image={cfg.image as string | undefined}
          />
        );
      case 'faq_preview':
        return (
          <FaqPreview
            key={s.id}
            heading={cfg.heading as string | undefined}
            itemCount={(cfg.item_count as number | undefined) ?? 4}
            faqs={faqs ?? []}
          />
        );
      case 'contact_banner':
        return (
          <ContactBanner
            key={s.id}
            heading={cfg.heading as string | undefined}
            body={cfg.body as string | undefined}
            ctaText={cfg.cta_text as string | undefined}
            ctaLink={cfg.cta_link as string | undefined}
            contactInfo={contactInfo}
          />
        );
      case 'product_grid':
        return (
          <ProductGridSection
            key={s.id}
            sectionId={s.id}
            title={s.title ?? ''}
            subtitle={cfg.subtitle as string | undefined}
            source={cfg.source as string | undefined}
            categoryId={cfg.category_id as number | undefined}
            productIds={cfg.product_ids as number[] | undefined}
            productCount={(cfg.product_count as number | undefined) ?? 8}
            showShopAll={(cfg.show_shop_all as boolean | undefined) !== false}
            shopAllUrl={(cfg.shop_all_url as string | undefined) ?? settings?.shop_all?.slug ?? '/shop'}
            bgColor={cfg.bg_color as string | undefined}
            textColor={cfg.text_color as string | undefined}
            sort={cfg.sort as string | undefined}
            display={cfg.display as SectionDisplayConfig | undefined}
            onQuickView={setQuickViewProduct}
          />
        );
      case 'category_showcase':
        return (
          <CategoryShowcaseSection
            key={s.id}
            sectionId={s.id}
            title={s.title ?? ''}
            subtitle={cfg.subtitle as string | undefined}
            categoryId={cfg.category_id as number | undefined}
            productCount={(cfg.product_count as number | undefined) ?? 8}
            showShopAll={(cfg.show_shop_all as boolean | undefined) != false}
            showBanner={(cfg.show_banner as boolean | undefined) != false}
            categories={displayCategories}
            sort={cfg.sort as string | undefined}
            display={cfg.display as SectionDisplayConfig | undefined}
            layout={cfg.layout as LayoutConfig | undefined}
            onQuickView={setQuickViewProduct}
          />
        );
      case 'sale_section':
        return (
          <SaleSectionSection
            key={s.id}
            sectionId={s.id}
            title={s.title ?? ''}
            subtitle={cfg.subtitle as string | undefined}
            productCount={(cfg.product_count as number | undefined) ?? 8}
            showCountdown={(cfg.show_countdown as boolean | undefined) ?? false}
            countdownEnd={cfg.countdown_end as string | undefined}
            showSaleBadge={(cfg.show_sale_badge as boolean | undefined) !== false}
            showOriginalPrice={(cfg.show_original_price as boolean | undefined) !== false}
            display={cfg.display as SectionDisplayConfig | undefined}
            layout={cfg.layout as LayoutConfig | undefined}
            onQuickView={setQuickViewProduct}
          />
        );
      case 'custom_product_list':
        return (
          <CustomProductListSection
            key={s.id}
            sectionId={s.id}
            title={s.title ?? ''}
            subtitle={cfg.subtitle as string | undefined}
            productIds={(cfg.product_ids as number[] | undefined) ?? []}
            layout={(cfg.layout as string | undefined) ?? 'grid'}
            onQuickView={setQuickViewProduct}
          />
        );
      case 'featured_bundles':
        return <FeaturedBundlesSection key={s.id} title={s.title ?? 'Bundles & Sets'} />;
      case 'bundle_showcase':
        return (
          <FeaturedBundlesSection
            key={s.id}
            title={s.title ?? 'Bundle Deals'}
            subtitle={cfg.subtitle as string | undefined}
            bundleIds={(cfg.bundle_ids as number[] | undefined) ?? []}
            bundleCount={(cfg.bundle_count as number | undefined) ?? 3}
            display={cfg.display as SectionDisplayConfig | undefined}
            layout={cfg.layout as LayoutConfig | undefined}
            showShopAll={(cfg.show_shop_all as boolean | undefined) !== false}
          />
        );

      case 'recently_viewed':
        return (
          <RecentlyViewedSection
            key={s.id}
            sectionId={s.id}
            title={s.title ?? 'Recently Viewed'}
            maxCount={(cfg.max_count as number | undefined) ?? 8}
            display={cfg.display as SectionDisplayConfig | undefined}
            layout={cfg.layout as LayoutConfig | undefined}
            onQuickView={setQuickViewProduct}
          />
        );
      case 'brand_showcase':
        return (
          <BrandShowcaseSection
            key={s.id}
            sectionId={s.id}
            title={s.title ?? ''}
            subtitle={cfg.subtitle as string | undefined}
            productCount={(cfg.product_count as number | undefined) ?? 8}
            showShopAll={(cfg.show_shop_all as boolean | undefined) !== false}
            display={cfg.display as SectionDisplayConfig | undefined}
            layout={cfg.layout as LayoutConfig | undefined}
            onQuickView={setQuickViewProduct}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div>
      {activeSections.map(s => renderSection(s))}
      <QuickViewModal product={quickViewProduct} onClose={() => setQuickViewProduct(null)} />
    </div>
  );
}

const DEFAULT_TRUST_ITEMS = [
  { title: 'Free Shipping',     copy: 'On orders over $75' },
  { title: 'Premium Materials', copy: 'Solid wood, archival ink' },
  { title: 'Secure Checkout',   copy: '256-bit SSL encrypted' },
  { title: '30-Day Returns',    copy: 'No questions asked' },
];

function FeaturedBundlesSection({
  title,
  subtitle,
  bundleIds = [],
  bundleCount = 3,
  display,
  layout,
  showShopAll = true,
}: {
  title: string;
  subtitle?: string;
  bundleIds?: number[];
  bundleCount?: number;
  display?: SectionDisplayConfig;
  layout?: LayoutConfig;
  showShopAll?: boolean;
}) {
  const { data: all, isLoading } = useBundles();
  const effectiveBundleIds = bundleIds;
  const effectiveBundleCount = bundleCount;
  const effectiveShowShopAll = showShopAll;
  const activeDisplay = layout ? layoutToDisplay(layout) : display;

  const bundles = effectiveBundleIds.length > 0
    ? effectiveBundleIds.map((id: number) => all?.find((b: BundleSummary) => b.id === id)).filter((b): b is NonNullable<typeof b> => b != null)
    : all;
  if (isLoading) return null;
  if (!bundles || bundles.length === 0) return null;
  const displayed = (effectiveBundleIds.length > 0 ? bundles : bundles.slice(0, effectiveBundleCount));

  return (
    <section className="py-12 md:py-16 bg-stone-50">
      <Container>
        <div className="mb-8 flex items-end justify-between">
          <div>
            <Eyebrow>Bundles</Eyebrow>
            <h2 className="mt-1 font-serif text-2xl font-medium text-stone-900 md:text-3xl">{title}</h2>
            {subtitle && <p className="mt-1 text-sm text-stone-500">{subtitle}</p>}
          </div>
          {effectiveShowShopAll && (
            <Link to="/bundles" className="text-sm text-stone-500 hover:text-stone-900 transition-colors">
              View all →
            </Link>
          )}
        </div>
        <SectionLayout
          items={displayed}
          display={activeDisplay ?? { display_mode: 'grid', columns_desktop: 3, columns_tablet: 2, columns_mobile: 1, spacing: 'medium' }}
          renderItem={(b: BundleSummary) => <BundleCard key={b.id} bundle={b} />}
        />
      </Container>
    </section>
  );
}

function TrustBar({ items }: { items?: { title: string; copy: string }[] }) {
  const display = Array.isArray(items) && items.length > 0 ? items : DEFAULT_TRUST_ITEMS;

  return (
    <section className="border-b border-stone-light bg-paper py-6 md:py-8">
      <Container className="grid grid-cols-2 gap-4 sm:grid-cols-4 sm:gap-6">
        {display.map((item) => (
          <div key={item.title} className="text-center">
            <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-ink md:text-xs">{item.title}</p>
            <p className="mt-1 text-[11px] text-stone md:text-xs">{item.copy}</p>
          </div>
        ))}
      </Container>
    </section>
  );
}

function BestSellers({
  products,
  isLoading,
  onQuickView,
  heading,
  subheading,
  productCount = 8,
  showShopAll = true,
  display,
}: {
  products: CatalogProduct[];
  isLoading: boolean;
  onQuickView: (p: CatalogProduct) => void;
  heading?: string;
  subheading?: string;
  productCount?: number;
  showShopAll?: boolean;
  display?: SectionDisplayConfig;
}) {
  const cfg = resolveDisplay(display);
  const limit = cfg.items_limit === 'all' ? productCount : Math.min(cfg.items_limit, productCount);
  const displayed = products.slice(0, limit);

  return (
    <section className="py-12 md:py-20">
      <Container>
        <div className="mb-8 flex items-end justify-between md:mb-10">
          <div>
            <Eyebrow>Best Sellers</Eyebrow>
            <h2 className="mt-2 font-serif text-2xl font-light text-ink md:mt-3 md:text-4xl">
              {heading ?? "Our customers' favourites."}
            </h2>
            {subheading && <p className="mt-1.5 text-sm text-stone md:mt-2">{subheading}</p>}
          </div>
          {showShopAll && (
            <Link
              to="/categories/wall-art"
              className="hidden shrink-0 border border-ink px-5 py-2.5 text-[12px] font-medium uppercase tracking-[0.1em] text-ink transition-colors hover:bg-ink hover:text-cream md:block"
            >
              Shop All
            </Link>
          )}
        </div>

        {isLoading ? (
          <div className="grid grid-cols-2 gap-5 md:grid-cols-4">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="aspect-[4/5] animate-pulse bg-stone-light/50" />
            ))}
          </div>
        ) : (
          <SectionLayout
            items={displayed}
            display={display}
            renderItem={(product) => (
              <ProductCard key={product.id} product={product} onQuickView={onQuickView} display={cardDisplayProps(display)} />
            )}
          />
        )}

        {showShopAll && (
          <div className="mt-8 text-center md:hidden">
            <Link
              to="/categories/wall-art"
              className="inline-block border border-ink px-8 py-3 text-[12px] font-medium uppercase tracking-[0.1em] text-ink transition-colors hover:bg-ink hover:text-cream"
            >
              Shop All
            </Link>
          </div>
        )}
      </Container>
    </section>
  );
}

function Collections({
  categories,
  heading,
  subheading,
  categoryIds,
  layout,
}: {
  categories: { id: number; name: string; slug: string; image?: string | null }[];
  heading?: string;
  subheading?: string;
  categoryIds?: number[];
  layout?: LayoutConfig;
}) {
  const copy = ['Shop the full edit', 'Structured & minimal', 'Bold colour, big impact', 'Timeless contrast'];

  // Determine which categories to display based on layout config or fallback
  let displayed: typeof categories;
  if (layout) {
    if (layout.dataSource === 'manual' && layout.selectedIds.length > 0) {
      displayed = layout.selectedIds
        .map(id => categories.find(c => c.id === id))
        .filter((c): c is typeof categories[0] => !!c);
    } else if (layout.dataSource === 'featured') {
      // No featured flag on public categories — show all, limited
      displayed = [...categories];
    } else {
      // 'all' — show all categories
      displayed = [...categories];
    }

    // Apply sorting (only for non-manual)
    if (layout.dataSource !== 'manual') {
      displayed = [...displayed].sort((a, b) => {
        switch (layout.sortOption) {
          case 'alphabetical_asc': return a.name.localeCompare(b.name);
          case 'alphabetical_desc': return b.name.localeCompare(a.name);
          case 'oldest': return a.id - b.id;
          case 'newest':
          default: return b.id - a.id;
        }
      });
    }

    // Apply item limit
    if (layout.itemLimit !== 'all' && typeof layout.itemLimit === 'number') {
      displayed = displayed.slice(0, layout.itemLimit);
    }
  } else {
    // Fallback: use categoryIds or first 4
    displayed = categoryIds && categoryIds.length > 0
      ? categoryIds.map(id => categories.find(c => c.id === id)).filter((c): c is typeof categories[0] => !!c)
      : categories.slice(0, 4);
  }

  // Responsive grid columns
  const gridCols = layout?.grid?.columns ?? { desktop: 4, tablet: 2, mobile: 2 };
  const gridGap = 20; // default gap

  // Section styling
  const sectionStyle: React.CSSProperties = layout ? {
    backgroundColor: layout.colors?.background || undefined,
    paddingTop: layout.spacing?.paddingTop ? `${layout.spacing.paddingTop}px` : undefined,
    paddingBottom: layout.spacing?.paddingBottom ? `${layout.spacing.paddingBottom}px` : undefined,
    marginTop: layout.spacing?.marginTop ? `${layout.spacing.marginTop}px` : undefined,
    marginBottom: layout.spacing?.marginBottom ? `${layout.spacing.marginBottom}px` : undefined,
  } : {};

  // Container max-width based on section width setting
  const containerMaxWidth = layout?.sectionWidth === 'boxed' ? 1024
    : layout?.sectionWidth === 'custom' ? layout.customMaxWidth
    : layout?.sectionWidth === 'full' ? '100%'
    : undefined;

  // Device detection for responsive columns
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  useEffect(() => {
    const checkDevice = () => {
      setIsMobile(window.innerWidth < 640);
      setIsTablet(window.innerWidth >= 640 && window.innerWidth < 1024);
    };
    checkDevice();
    window.addEventListener('resize', checkDevice);
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  // Device visibility
  if (layout?.advanced?.hideOnMobile && isMobile) return null;
  if (layout?.advanced?.hideOnDesktop && !isMobile && !isTablet) return null;

  // Image aspect ratio
  const getAspectRatio = () => {
    const ratio = layout?.image?.ratio ?? 'portrait';
    const ratioMap: Record<string, string> = {
      'square': 'aspect-square',
      'portrait': 'aspect-[4/5]',
      'landscape': 'aspect-[4/3]',
      'original': '',
    };
    return ratioMap[ratio] || 'aspect-[4/5]';
  };

  // Current responsive column count
  const currentCols = isMobile ? gridCols.mobile : isTablet ? gridCols.tablet : gridCols.desktop;

  // Carousel slides per view (responsive)
  const carouselSlides = layout?.carousel?.slidesPerView ?? { desktop: 4, tablet: 2, mobile: 1 };
  const carouselGap = layout?.carousel?.spaceBetween ?? 16;

  // Show empty state if no items
  if (displayed.length === 0) {
    return (
      <section className="bg-paper py-12 md:py-20">
        <Container>
          <p className="text-center text-sm text-neutral-400">No categories to display.</p>
        </Container>
      </section>
    );
  }

  return (
    <section
      id={layout?.advanced?.sectionId || 'collections'}
      className={`bg-paper py-12 md:py-20 ${layout?.advanced?.customClass || ''}`}
      style={sectionStyle}
    >
      <Container style={containerMaxWidth ? { maxWidth: containerMaxWidth } : undefined}>
        <div className="mb-6 md:mb-10">
          <Eyebrow>Collections</Eyebrow>
          <h2 className="mt-2 font-serif text-2xl font-light text-ink md:mt-3 md:text-4xl" style={{ color: layout?.colors?.text }}>
            {heading ?? 'Shop by collection.'}
          </h2>
          {subheading && (
            <p className="mt-2 text-sm text-stone md:text-base" style={{ color: layout?.colors?.text ? `${layout.colors.text}99` : undefined }}>
              {subheading}
            </p>
          )}
        </div>

        {layout?.displayMode === 'carousel' ? (
          <SectionCarousel
            items={displayed}
            renderItem={(category, i) => (
              <CategoryCard
                name={category.name}
                copy={layout.cardComponents?.showDescription ? copy[i % copy.length] : ''}
                seed={`category-${category.slug}`}
                to={`/categories/${category.slug}`}
                image={category.image}
                aspect={getAspectRatio()}
              />
            )}
            config={{
              slidesDesktop: carouselSlides.desktop,
              slidesTablet: carouselSlides.tablet,
              slidesMobile: carouselSlides.mobile,
              gap: carouselGap,
              loop: layout.carousel?.loop ?? false,
              autoplay: layout.carousel?.autoplay ?? false,
              autoplaySpeed: layout.carousel?.autoplaySpeed ?? 5000,
              pauseOnHover: layout.carousel?.pauseOnHover ?? true,
              drag: layout.carousel?.mouseDrag ?? true,
              touch: layout.carousel?.touchSwipe ?? true,
              navigation: layout.carousel?.navigation ?? true,
              pagination: layout.carousel?.pagination ?? true,
            }}
          />
        ) : (
          <div
            className="grid"
            style={{
              gridTemplateColumns: `repeat(${currentCols}, 1fr)`,
              gap: `${gridGap}px`,
            }}
          >
            {displayed.map((category, i) => (
              <CategoryCard
                key={category.id}
                name={category.name}
                copy={layout?.cardComponents?.showDescription ? copy[i % copy.length] : ''}
                seed={`category-${category.slug}`}
                to={`/categories/${category.slug}`}
                image={category.image}
                aspect={getAspectRatio()}
              />
            ))}
          </div>
        )}
      </Container>
    </section>
  );
}

function CustomPrintBanner({
  eyebrow,
  heading,
  body,
  ctaText,
  ctaLink,
  badgeText,
  badgeVisible,
  image,
}: {
  eyebrow?: string;
  heading?: string;
  body?: string;
  ctaText?: string;
  ctaLink?: string;
  badgeText?: string;
  badgeVisible?: boolean;
  image?: string;
}) {
  const imageSrc = image ? (image.startsWith('http') ? image : `${API_URL}${image}`) : null;

  return (
    <section id="custom" className="relative overflow-hidden bg-ink">
      <div className="grid md:grid-cols-2">
        <div className="flex flex-col justify-center px-6 py-10 md:px-16 md:py-24">
          <Eyebrow dark>{eyebrow ?? 'Custom Prints'}</Eyebrow>
          <h2 className="text-balance mt-4 max-w-md font-serif text-2xl font-light leading-tight text-cream md:mt-5 md:text-4xl">
            {heading ?? "Got a photo you love? We'll frame it."}
          </h2>
          {badgeVisible !== false && badgeText && (
            <span className="mt-3 inline-block w-fit rounded-full border border-cream/20 bg-cream/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-cream/70">
              {badgeText}
            </span>
          )}
          <p className="mt-4 max-w-sm text-sm leading-relaxed text-cream/70">
            {body ?? "Upload any image and we'll print it on the same archival stock and solid-wood frames as every edition in the shop. From $58."}
          </p>
          <div className="mt-6 md:mt-8">
            <Link
              to={ctaLink ?? '/custom-prints'}
              className="inline-block bg-cream px-6 py-3 text-[12px] font-semibold uppercase tracking-[0.1em] text-ink transition-colors hover:bg-clay hover:text-cream md:px-7 md:py-3.5 md:text-[13px]"
            >
              {ctaText ?? 'Start Your Custom Print'}
            </Link>
          </div>
        </div>
        {imageSrc ? (
          <div className="aspect-[4/3] overflow-hidden md:aspect-auto md:h-full">
            <img src={imageSrc} alt="Custom print banner" className="h-full w-full object-cover" />
          </div>
        ) : (
          <EditorialImage
            seed="custom-print-upload"
            alt="A custom framed photo print"
            duotone={false}
            size="large"
            aspect="aspect-[4/3] md:aspect-auto md:h-full"
          />
        )}
      </div>
    </section>
  );
}

function ReviewCard({
  review,
  onImageClick,
}: {
  review: PublicReview;
  onImageClick: (images: string[], idx: number) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const imgSrc = (src: string) => (src.startsWith('http') ? src : `${API_URL}${src}`);

  const artworkSrc = review.images?.[0] ?? null;
  const hasHeader = !!(review.customer_photo || review.customer_name || review.rating > 0);
  const hasFooter = !!(review.product || review.review_date);
  const hasBody = !!(review.title || review.comment);

  return (
    <div className="group flex flex-col bg-white rounded-2xl overflow-hidden shadow-[0_2px_16px_rgba(0,0,0,0.06)] transition-all duration-500 ease-out hover:shadow-[0_20px_48px_rgba(0,0,0,0.12)] hover:-translate-y-1">
      {/* Artwork image — primary focus */}
      {artworkSrc ? (
        <button
          type="button"
          className="relative w-full shrink-0 overflow-hidden bg-[#F4F2EE] aspect-[4/3]"
          onClick={() => onImageClick(review.images, 0)}
        >
          <img
            src={imgSrc(artworkSrc)}
            alt="Customer artwork"
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
          />
          {review.images.length > 1 && (
            <span className="absolute bottom-3 right-3 flex items-center gap-1 rounded-full bg-black/30 px-2.5 py-1 text-[10px] font-medium text-white/90 backdrop-blur-sm">
              +{review.images.length - 1}
            </span>
          )}
        </button>
      ) : (
        <div className="relative w-full shrink-0 bg-[#F4F2EE] aspect-[4/3] flex items-center justify-center">
          <svg className="h-10 w-10 text-stone-300" fill="none" viewBox="0 0 24 24">
            <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.2" />
            <path d="M3 16l4.5-4.5 3.5 3.5 2.5-2.5L18 17" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
            <circle cx="8" cy="8.5" r="1.5" fill="currentColor" />
          </svg>
        </div>
      )}

      {/* Card body */}
      <div className="flex flex-col flex-1 px-5 pt-4 pb-5 md:px-6 md:pb-6">
        {/* Header: avatar · name · stars (all optional) */}
        {hasHeader && (
          <div className="flex items-center gap-2.5 mb-3">
            {review.customer_photo && (
              <img
                src={imgSrc(review.customer_photo)}
                alt={review.customer_name}
                className="h-8 w-8 rounded-full object-cover ring-1 ring-stone-100 shrink-0"
              />
            )}
            <div className="flex-1 min-w-0">
              {review.customer_name && (
                <p className="text-[13px] font-medium text-ink leading-none truncate">{review.customer_name}</p>
              )}
              {(review.customer_country || review.company) && (
                <p className="mt-0.5 text-[11px] text-stone/55 truncate">
                  {[review.company, review.customer_country].filter(Boolean).join(' · ')}
                </p>
              )}
            </div>
            {review.rating > 0 && (
              <div className="shrink-0 flex gap-px leading-none">
                {[1, 2, 3, 4, 5].map((s) => (
                  <span key={s} className="text-[13px]" style={{ color: s <= review.rating ? '#C9A84C' : '#DDD8D2' }}>
                    ★
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Review title */}
        {review.title && (
          <p className="font-serif text-sm font-medium text-ink leading-snug mb-2">{review.title}</p>
        )}

        {/* Comment */}
        {review.comment && (
          <div className="flex-1">
            <p className={`text-[13px] leading-relaxed text-stone ${!expanded ? 'line-clamp-3' : ''}`}>
              &ldquo;{review.comment}&rdquo;
            </p>
            {!expanded && review.comment.length > 160 && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setExpanded(true); }}
                className="mt-1.5 text-[11px] text-stone/45 hover:text-ink transition-colors"
              >
                Read more
              </button>
            )}
          </div>
        )}

        {/* Footer: product + date (both optional) */}
        {hasFooter && (
          <div
            className={`flex items-center justify-between gap-2 min-w-0 ${
              hasBody ? 'mt-3.5 pt-3 border-t border-[#F0ECE6]' : 'mt-1'
            }`}
          >
            {review.product ? (
              <p className="text-[11px] text-stone/45 truncate">{review.product.name}</p>
            ) : (
              <span />
            )}
            {review.review_date && (
              <p className="shrink-0 text-[11px] text-stone/35">
                {new Date(review.review_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ImageLightbox({
  images,
  initialIdx,
  onClose,
}: {
  images: string[];
  initialIdx: number;
  onClose: () => void;
}) {
  const [idx, setIdx] = useState(initialIdx);
  const src = images[idx];
  const fullSrc = src ? (src.startsWith('http') ? src : `${API_URL}${src}`) : '';

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') setIdx((i) => Math.max(0, i - 1));
      if (e.key === 'ArrowRight') setIdx((i) => Math.min(images.length - 1, i + 1));
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [images.length, onClose]);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4"
      onClick={onClose}
    >
      <div className="relative max-h-[85vh] max-w-3xl" onClick={(e) => e.stopPropagation()}>
        <img src={fullSrc} alt="" className="max-h-[80vh] max-w-full rounded-xl object-contain" />
        <button
          type="button"
          onClick={onClose}
          className="absolute -right-3 -top-3 flex h-8 w-8 items-center justify-center rounded-full bg-white text-sm text-ink shadow-lg hover:bg-stone-50 transition-colors"
        >
          ✕
        </button>
        {images.length > 1 && (
          <>
            <button
              type="button"
              onClick={() => setIdx((i) => Math.max(0, i - 1))}
              disabled={idx === 0}
              className="absolute left-2 top-1/2 -translate-y-1/2 flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur-sm hover:bg-white/20 disabled:opacity-20 transition-all"
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M10 12L6 8l4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </button>
            <button
              type="button"
              onClick={() => setIdx((i) => Math.min(images.length - 1, i + 1))}
              disabled={idx === images.length - 1}
              className="absolute right-2 top-1/2 -translate-y-1/2 flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur-sm hover:bg-white/20 disabled:opacity-20 transition-all"
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </button>
          </>
        )}
        {images.length > 1 && (
          <div className="mt-3 flex justify-center gap-1.5 overflow-x-auto">
            {images.map((img, i) => {
              const s = img.startsWith('http') ? img : `${API_URL}${img}`;
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => setIdx(i)}
                  className={`h-11 w-11 shrink-0 overflow-hidden rounded-md transition-all ${
                    i === idx ? 'ring-2 ring-white' : 'opacity-50 hover:opacity-80'
                  }`}
                >
                  <img src={s} alt="" className="h-full w-full object-cover" />
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function Reviews({
  heading,
  eyebrow,
  limit = 12,
  autoplaySpeed = 5000,
}: {
  heading?: string;
  eyebrow?: string;
  limit?: number;
  autoplaySpeed?: number;
}) {
  const { data: liveReviews = [] } = useReviews(limit);
  const [currentPage, setCurrentPage] = useState(0);
  const [perPage, setPerPage] = useState(3);
  const [dragStart, setDragStart] = useState<number | null>(null);
  const [lightbox, setLightbox] = useState<{ images: string[]; idx: number } | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const update = () => setPerPage(window.innerWidth < 640 ? 1 : window.innerWidth < 1024 ? 2 : 3);
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const reviews = liveReviews;
  const hasLive = reviews.length > 0;
  const totalPages = Math.max(1, Math.ceil(reviews.length / perPage));
  const safePage = ((currentPage % totalPages) + totalPages) % totalPages;

  const goTo = (p: number) => setCurrentPage(((p % totalPages) + totalPages) % totalPages);

  useEffect(() => {
    if (autoplaySpeed <= 0 || reviews.length <= perPage) return;
    intervalRef.current = setInterval(
      () => setCurrentPage((c) => (c + 1) % totalPages),
      autoplaySpeed,
    );
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [autoplaySpeed, reviews.length, perPage, totalPages]);

  const handleDragEnd = (endX: number) => {
    if (dragStart === null) return;
    const diff = endX - dragStart;
    if (Math.abs(diff) > 60) goTo(diff < 0 ? safePage + 1 : safePage - 1);
    setDragStart(null);
  };

  const pageGroups = Array.from({ length: totalPages }, (_, p) =>
    reviews.slice(p * perPage, p * perPage + perPage),
  );

  return (
    <section className="py-16 md:py-28" style={{ background: '#F9F7F4' }}>
      <Container>
        <div className="mb-12 text-center md:mb-16">
          <Eyebrow>{eyebrow ?? 'Customer Stories'}</Eyebrow>
          <h2 className="mt-3 font-serif text-3xl font-light text-ink md:mt-4 md:text-5xl leading-tight">
            {heading ?? 'Loved by collectors worldwide.'}
          </h2>
        </div>
      </Container>

      {!hasLive ? (
        <Container>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {REVIEWS.map((r) => (
              <div key={r.name} className="bg-white rounded-2xl px-6 py-6 shadow-[0_2px_16px_rgba(0,0,0,0.06)]">
                <div className="flex gap-px mb-4">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <span key={s} className="text-sm" style={{ color: '#C9A84C' }}>★</span>
                  ))}
                </div>
                <p className="font-serif text-sm text-ink leading-relaxed mb-4">&ldquo;{r.quote}&rdquo;</p>
                <p className="text-[11px] font-medium uppercase tracking-[0.1em] text-stone/50">{r.name}</p>
              </div>
            ))}
          </div>
        </Container>
      ) : (
        <div className="relative">
          {/* Crossfade carousel — all pages stacked in the same grid cell via [grid-area:1/1/2/2] */}
          <div
            className="grid px-4 md:px-8 lg:px-16"
            style={{ cursor: dragStart !== null ? 'grabbing' : 'default' }}
            onMouseDown={(e) => setDragStart(e.clientX)}
            onMouseUp={(e) => handleDragEnd(e.clientX)}
            onMouseLeave={() => setDragStart(null)}
            onTouchStart={(e) => setDragStart(e.touches[0].clientX)}
            onTouchEnd={(e) => handleDragEnd(e.changedTouches[0].clientX)}
          >
            {pageGroups.map((group, p) => (
              <div
                key={p}
                className={`[grid-area:1/1/2/2] transition-opacity duration-700 ease-in-out ${
                  p === safePage ? 'opacity-100' : 'opacity-0 pointer-events-none'
                }`}
              >
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {group.map((r) => (
                    <ReviewCard
                      key={r.id}
                      review={r}
                      onImageClick={(imgs, idx) => setLightbox({ images: imgs, idx })}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Navigation arrows */}
          {totalPages > 1 && (
            <>
              <button
                type="button"
                onClick={() => goTo(safePage - 1)}
                aria-label="Previous reviews"
                className="absolute left-1 top-1/3 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 shadow-md text-ink transition-all duration-300 hover:shadow-lg hover:bg-white md:left-3"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M10 12L6 8l4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              <button
                type="button"
                onClick={() => goTo(safePage + 1)}
                aria-label="Next reviews"
                className="absolute right-1 top-1/3 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 shadow-md text-ink transition-all duration-300 hover:shadow-lg hover:bg-white md:right-3"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </>
          )}
        </div>
      )}

      {/* Pagination dots */}
      {hasLive && totalPages > 1 && (
        <div className="mt-8 flex justify-center items-center gap-2">
          {Array.from({ length: totalPages }).map((_, p) => (
            <button
              key={p}
              type="button"
              onClick={() => goTo(p)}
              aria-label={`Go to page ${p + 1}`}
              className={`rounded-full transition-all duration-300 ${
                p === safePage ? 'w-6 h-1.5 bg-ink' : 'w-1.5 h-1.5 bg-[#C8C2BA] hover:bg-stone'
              }`}
            />
          ))}
        </div>
      )}

      {lightbox && (
        <ImageLightbox
          images={lightbox.images}
          initialIdx={lightbox.idx}
          onClose={() => setLightbox(null)}
        />
      )}
    </section>
  );
}

function Instagram({ handle, heading, images }: { handle?: string; heading?: string; images?: string[] }) {
  const hasUploaded = Array.isArray(images) && images.length > 0;

  return (
    <section className="bg-paper py-12 md:py-20">
      <Container>
        <div className="mb-6 text-center md:mb-8">
          <Eyebrow>{handle ?? '@framedstudio'}</Eyebrow>
          <h2 className="mt-2 font-serif text-2xl font-light text-ink md:mt-3 md:text-4xl">{heading ?? 'Shop the look.'}</h2>
        </div>
        <div className="grid grid-cols-3 gap-1.5 md:grid-cols-6 md:gap-2">
          {hasUploaded
            ? images!.map((path, i) => {
                const src = path.startsWith('http') ? path : `${API_URL}${path}`;
                return (
                  <div key={i} className="group relative aspect-square overflow-hidden">
                    <img
                      src={src}
                      alt={`${handle ?? 'Instagram'} photo ${i + 1}`}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-ink/0 transition-colors group-hover:bg-ink/30">
                      <span className="text-lg text-cream opacity-0 transition-opacity group-hover:opacity-100">+</span>
                    </div>
                  </div>
                );
              })
            : INSTAGRAM_SEEDS.map((seed) => (
                <div key={seed} className="group relative aspect-square overflow-hidden">
                  <EditorialImage
                    seed={seed}
                    alt="Customer photo of a framed print"
                    duotone={false}
                    size="thumb"
                    aspect="aspect-square"
                    className="transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-ink/0 transition-colors group-hover:bg-ink/30">
                    <span className="text-lg text-cream opacity-0 transition-opacity group-hover:opacity-100">+</span>
                  </div>
                </div>
              ))}
        </div>
      </Container>
    </section>
  );
}

function AboutBanner({ eyebrow, heading, body, ctaText, ctaLink, image }: {
  eyebrow?: string; heading?: string; body?: string; ctaText?: string; ctaLink?: string; image?: string;
}) {
  const imgSrc = image ? (image.startsWith('http') ? image : `${API_URL}${image}`) : null;
  return (
    <section className="bg-paper py-12 md:py-24">
      <Container>
        <div className="grid items-center gap-8 md:grid-cols-2 md:gap-12">
          <div className="order-2 md:order-1">
            <Eyebrow>{eyebrow ?? 'Our Story'}</Eyebrow>
            <h2 className="mt-3 font-serif text-2xl font-light text-ink md:mt-4 md:text-4xl">
              {heading ?? 'Crafted with purpose, framed with care.'}
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-stone md:mt-5">
              {body ?? "We started with a single belief: great art deserves a great frame. Every product we make is designed to last a lifetime."}
            </p>
            <div className="mt-6 md:mt-8">
              <Link to={ctaLink ?? '/about'}
                className="inline-flex items-center gap-2 border border-ink px-6 py-3 text-[12px] font-semibold uppercase tracking-[0.1em] text-ink transition-colors hover:bg-ink hover:text-cream">
                {ctaText ?? 'Learn More'}
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6h8M6 2l4 4-4 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </Link>
            </div>
          </div>
          {imgSrc ? (
            <div className="order-1 aspect-[4/3] overflow-hidden md:order-2">
              <img src={imgSrc} alt="About" className="h-full w-full object-cover" />
            </div>
          ) : (
            <EditorialImage seed="about-banner" alt="About us" duotone={false} size="large" aspect="order-1 aspect-[4/3] md:order-2" />
          )}
        </div>
      </Container>
    </section>
  );
}

function FaqPreview({ heading, itemCount, faqs }: {
  heading?: string; itemCount: number; faqs: { id: number; question: string; answer: string; is_visible?: boolean }[];
}) {
  const [openId, setOpenId] = useState<number | null>(null);
  const visible = faqs.filter(f => f.is_visible !== false).slice(0, itemCount);

  if (visible.length === 0) return null;

  return (
    <section className="py-12 md:py-20">
      <Container>
        <div className="mb-8 flex items-end justify-between md:mb-10">
          <div>
            <Eyebrow>Help</Eyebrow>
            <h2 className="mt-2 font-serif text-2xl font-light text-ink md:mt-3 md:text-4xl">
              {heading ?? 'Frequently Asked Questions'}
            </h2>
          </div>
          <Link to="/faqs"
            className="hidden shrink-0 border border-ink px-5 py-2.5 text-[12px] font-medium uppercase tracking-[0.1em] text-ink transition-colors hover:bg-ink hover:text-cream md:block">
            View All
          </Link>
        </div>
        <div className="divide-y divide-stone-light border-y border-stone-light">
          {visible.map(faq => (
            <div key={faq.id}>
              <button
                type="button"
                onClick={() => setOpenId(openId === faq.id ? null : faq.id)}
                className="flex w-full items-center justify-between gap-4 py-4 text-left md:py-5"
              >
                <span className="text-sm font-medium text-ink">{faq.question}</span>
                <span className="shrink-0 text-lg text-stone">{openId === faq.id ? '−' : '+'}</span>
              </button>
              {openId === faq.id && (
                <p className="pb-4 text-sm leading-relaxed text-stone md:pb-5">{faq.answer}</p>
              )}
            </div>
          ))}
        </div>
        <div className="mt-6 text-center md:hidden">
          <Link to="/faqs"
            className="inline-block border border-ink px-8 py-3 text-[12px] font-medium uppercase tracking-[0.1em] text-ink transition-colors hover:bg-ink hover:text-cream">
            View All FAQs
          </Link>
        </div>
      </Container>
    </section>
  );
}

function ContactBanner({ heading, body, ctaText, ctaLink, contactInfo }: {
  heading?: string; body?: string; ctaText?: string; ctaLink?: string;
  contactInfo?: { address?: string | null; phone?: string | null; email?: string | null; working_hours?: { sun_thu?: string; fri_sat?: string } | null } | null;
}) {
  const details = [
    contactInfo?.address && { icon: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z', label: 'Address', value: contactInfo.address },
    contactInfo?.phone && { icon: 'M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z', label: 'Phone', value: contactInfo.phone, href: `tel:${contactInfo.phone}` },
    contactInfo?.email && { icon: 'M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z', label: 'Email', value: contactInfo.email, href: `mailto:${contactInfo.email}` },
  ].filter(Boolean) as { icon: string; label: string; value: string; href?: string }[];

  return (
    <section className="bg-paper py-12 md:py-20">
      <Container>
        <div className="grid items-center gap-8 md:grid-cols-2 md:gap-12">
          <div>
            <Eyebrow>Contact</Eyebrow>
            <h2 className="mt-3 font-serif text-2xl font-light text-ink md:mt-4 md:text-4xl">
              {heading ?? "We'd love to hear from you."}
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-stone md:mt-5">
              {body ?? 'Have a question about an order, or just want to say hello? Our team is here to help.'}
            </p>
            <div className="mt-6 md:mt-8">
              <Link to={ctaLink ?? '/contact'}
                className="inline-flex items-center gap-2 bg-ink px-6 py-3 text-[12px] font-semibold uppercase tracking-[0.1em] text-cream transition-colors hover:bg-clay hover:text-cream md:px-7 md:py-3.5 md:text-[13px]">
                {ctaText ?? 'Get in Touch'}
              </Link>
            </div>
          </div>
          {details.length > 0 && (
            <div className="space-y-5 border-t border-stone-light pt-8 md:space-y-6 md:border-l md:border-t-0 md:pl-12 md:pt-0">
              {details.map(d => (
                <div key={d.label} className="flex items-start gap-4">
                  <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-stone-light/50">
                    <svg viewBox="0 0 24 24" className="h-4 w-4 fill-stone"><path d={d.icon}/></svg>
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-stone">{d.label}</p>
                    {d.href ? (
                      <a href={d.href} className="mt-1 block text-sm text-ink hover:text-clay">{d.value}</a>
                    ) : (
                      <p className="mt-1 text-sm text-ink">{d.value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Container>
    </section>
  );
}

function NewsletterBanner({
  heading,
  body,
  buttonText,
}: {
  heading?: string;
  body?: string;
  buttonText?: string;
}) {
  return (
    <section className="bg-clay py-16 md:py-20">
      <Container className="flex flex-col items-center text-center">
        <h2 className="font-serif text-3xl font-light text-cream md:text-4xl">
          {heading ?? 'Get 10% off your first order.'}
        </h2>
        <p className="mt-3 max-w-sm text-sm text-cream/85">
          {body ?? 'Join our list for early access to new editions and members-only offers.'}
        </p>
        <form
          className="mt-7 flex w-full max-w-sm items-center gap-2"
          onSubmit={(e) => e.preventDefault()}
        >
          <input
            type="email"
            required
            placeholder="Your email"
            className="w-full bg-cream px-4 py-3 text-sm text-ink placeholder:text-stone focus:outline-none"
          />
          <button
            type="submit"
            className="shrink-0 bg-ink px-5 py-3 text-[12px] font-semibold uppercase tracking-[0.1em] text-cream hover:bg-ink-soft"
          >
            {buttonText ?? 'Join'}
          </button>
        </form>
      </Container>
    </section>
  );
}

// ─── Dynamic section: Product Grid ────────────────────────────────────────────

function ProductGridSection({
  sectionId: _sectionId, title, subtitle, source, categoryId, productIds, productCount,
  showShopAll, shopAllUrl, bgColor, textColor, sort, display, onQuickView,
}: {
  sectionId: number; title: string; subtitle?: string; source?: string;
  categoryId?: number; productIds?: number[]; productCount: number;
  showShopAll: boolean; shopAllUrl: string;
  bgColor?: string; textColor?: string; sort?: string;
  display?: SectionDisplayConfig;
  onQuickView: (p: CatalogProduct) => void;
}) {
  const cfg = resolveDisplay(display);
  const limit = cfg.items_limit === 'all' ? productCount : cfg.items_limit;

  const { data: products = [], isLoading } = useSectionProducts({
    source: source ?? 'new_arrivals',
    limit,
    category_id: categoryId,
    product_ids: productIds,
    sort,
  });

  if (!isLoading && products.length === 0) return null;

  return (
    <section className="py-12 md:py-20" style={{ background: bgColor, color: textColor }}>
      <Container>
        <div className="mb-8 flex items-end justify-between md:mb-10">
          <div>
            {title && <h2 className="font-serif text-2xl font-light md:text-4xl" style={{ color: textColor }}>{title}</h2>}
            {subtitle && <p className="mt-1.5 text-sm opacity-70 md:mt-2">{subtitle}</p>}
          </div>
          {showShopAll && (
            <Link to={shopAllUrl}
              className="hidden shrink-0 border border-current px-5 py-2.5 text-[12px] font-medium uppercase tracking-[0.1em] transition-colors hover:opacity-70 md:block">
              Shop All
            </Link>
          )}
        </div>
        {isLoading ? (
          <div className="grid grid-cols-2 gap-5 md:grid-cols-4">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="aspect-[4/5] animate-pulse bg-stone-light/50" />
            ))}
          </div>
        ) : (
          <SectionLayout
            items={products}
            display={display}
            renderItem={(p) => <ProductCard key={p.id} product={p} onQuickView={onQuickView} display={cardDisplayProps(display)} />}
          />
        )}
        {showShopAll && (
          <div className="mt-8 text-center md:hidden">
            <Link to={shopAllUrl}
              className="inline-block border border-current px-8 py-3 text-[12px] font-medium uppercase tracking-[0.1em] transition-colors hover:opacity-70">
              Shop All
            </Link>
          </div>
        )}
      </Container>
    </section>
  );
}

// ─── Dynamic section: Category Showcase ───────────────────────────────────────

function CategoryShowcaseSection({
  sectionId: _sectionId, title, subtitle, categoryId: categoryIdProp, productCount, showShopAll: showShopAllProp,
  showBanner: showBannerProp, categories, sort, display, layout, onQuickView,
}: {
  sectionId: number; title: string; subtitle?: string; categoryId?: number;
  productCount: number; showShopAll: boolean; showBanner: boolean;
  categories: { id: number; name: string; slug: string; image?: string | null; banner_image?: string | null }[];
  sort?: string;
  display?: SectionDisplayConfig;
  layout?: LayoutConfig;
  onQuickView: (p: CatalogProduct) => void;
}) {
  // Prefer new LayoutConfig fields when saved by the Visual Builder
  const categoryId = categoryIdProp;
  const showShopAll = showShopAllProp;
  const showBanner = showBannerProp;

  // Build display config from layout or fall back to old display config
  const displayFromLayout: SectionDisplayConfig | undefined = layout ? {
    display_mode: layout.displayMode === 'carousel' ? 'carousel' : 'grid',
    columns_desktop: layout.grid?.columns?.desktop ?? 4,
    columns_tablet: layout.grid?.columns?.tablet ?? 2,
    columns_mobile: layout.grid?.columns?.mobile ?? 2,
    rows: layout.grid?.rows?.desktop ?? 2,
    spacing: 'medium',
    card_style: 'standard',
    image_ratio: layout.image?.ratio === 'square' ? 'square' : layout.image?.ratio === 'landscape' ? 'landscape' : 'portrait',
    items_limit: typeof layout.itemLimit === 'number' ? layout.itemLimit : 'all',
    carousel_slides_desktop: layout.carousel?.slidesPerView?.desktop ?? 4,
    carousel_slides_tablet: layout.carousel?.slidesPerView?.tablet ?? 2,
    carousel_slides_mobile: layout.carousel?.slidesPerView?.mobile ?? 1,
    carousel_space_between: layout.carousel?.spaceBetween ?? 20,
    carousel_loop: layout.carousel?.loop ?? false,
    carousel_autoplay: layout.carousel?.autoplay ?? false,
    carousel_autoplay_speed: layout.carousel?.autoplaySpeed ?? 5000,
    carousel_pause_on_hover: layout.carousel?.pauseOnHover ?? true,
    carousel_drag: layout.carousel?.mouseDrag ?? true,
    carousel_touch: layout.carousel?.touchSwipe ?? true,
    carousel_navigation: layout.carousel?.navigation ?? true,
    carousel_pagination: layout.carousel?.pagination ?? true,
    show_image: layout.cardComponents?.showImage ?? true,
    show_title: layout.cardComponents?.showName ?? true,
    show_price: true,
    show_badges: true,
    show_wishlist: true,
    show_quick_view: true,
    show_add_to_cart: true,
    show_discount_badge: true,
  } : display;

  const activeDisplay = displayFromLayout ?? display;
  const cfg = resolveDisplay(activeDisplay);
  const limit = cfg.items_limit === 'all' ? productCount : cfg.items_limit;
  const sortKey = layout?.sortOption ?? sort;

  const { data: products = [], isLoading } = useSectionProducts({
    limit, category_id: categoryId, sort: sortKey,
  });
  const category = categories.find(c => c.id === categoryId);
  const bannerPath = showBanner ? (category?.banner_image ?? category?.image) : null;
  const imgSrc = bannerPath ? (bannerPath.startsWith('http') ? bannerPath : `${API_URL}${bannerPath}`) : null;

  // Section background from layout colors
  const sectionStyle: React.CSSProperties = layout?.colors?.background
    ? { backgroundColor: layout.colors.background }
    : {};
  const paddingStyle: React.CSSProperties = layout?.spacing ? {
    paddingTop: layout.spacing.paddingTop ? `${layout.spacing.paddingTop}px` : undefined,
    paddingBottom: layout.spacing.paddingBottom ? `${layout.spacing.paddingBottom}px` : undefined,
  } : {};

  if (!isLoading && products.length === 0) return null;

  return (
    <section className="bg-paper" style={{ ...sectionStyle, ...paddingStyle, paddingTop: paddingStyle.paddingTop ?? '3rem', paddingBottom: paddingStyle.paddingBottom ?? '5rem' }}>
      <Container>
        {imgSrc && (
          <div className="mb-6 h-40 overflow-hidden rounded-lg md:mb-8 md:h-64">
            <img src={imgSrc} alt={category?.name} className="h-full w-full object-cover" />
          </div>
        )}
        <div className="mb-8 flex items-end justify-between md:mb-10">
          <div>
            <Eyebrow>{category?.name ?? 'Collection'}</Eyebrow>
            {title && <h2 className="mt-2 font-serif text-2xl font-light text-ink md:mt-3 md:text-4xl">{title}</h2>}
            {subtitle && <p className="mt-1.5 text-sm text-stone md:mt-2">{subtitle}</p>}
          </div>
          {showShopAll && category && (
            <Link to={`/categories/${category.slug}`}
              className="hidden shrink-0 border border-ink px-5 py-2.5 text-[12px] font-medium uppercase tracking-[0.1em] text-ink transition-colors hover:bg-ink hover:text-cream md:block">
              Shop All
            </Link>
          )}
        </div>
        {isLoading ? (
          <div className="grid grid-cols-2 gap-5 md:grid-cols-4">
            {[0, 1, 2, 3].map((i) => <div key={i} className="aspect-[4/5] animate-pulse bg-stone-light/50" />)}
          </div>
        ) : (
          <SectionLayout
            items={products}
            display={activeDisplay}
            renderItem={(p) => <ProductCard key={p.id} product={p} onQuickView={onQuickView} display={cardDisplayProps(activeDisplay)} />}
          />
        )}
        {showShopAll && category && (
          <div className="mt-8 text-center md:hidden">
            <Link to={`/categories/${category.slug}`}
              className="inline-block border border-ink px-8 py-3 text-[12px] font-medium uppercase tracking-[0.1em] text-ink transition-colors hover:bg-ink hover:text-cream">
              Shop All
            </Link>
          </div>
        )}
      </Container>
    </section>
  );
}

// ─── Dynamic section: Sale Section ────────────────────────────────────────────

function useCountdown(endDate?: string) {
  const [remaining, setRemaining] = useState(0);
  useEffect(() => {
    if (!endDate) return;
    const target = new Date(endDate).getTime();
    const tick = () => setRemaining(Math.max(0, target - Date.now()));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [endDate]);
  if (!endDate) return null;
  const d = Math.floor(remaining / 86400000);
  const h = Math.floor((remaining % 86400000) / 3600000);
  const m = Math.floor((remaining % 3600000) / 60000);
  const s = Math.floor((remaining % 60000) / 1000);
  return remaining > 0 ? { d, h, m, s } : null;
}

function SaleSectionSection({
  sectionId: _sectionId, title, subtitle, productCount, showCountdown, countdownEnd,
  showSaleBadge: _showSaleBadge, showOriginalPrice: _showOriginalPrice, display, layout, onQuickView,
}: {
  sectionId: number; title: string; subtitle?: string; productCount: number;
  showCountdown: boolean; countdownEnd?: string; showSaleBadge: boolean;
  showOriginalPrice: boolean; display?: SectionDisplayConfig; layout?: LayoutConfig;
  onQuickView: (p: CatalogProduct) => void;
}) {
  const activeDisplay = layout ? layoutToDisplay(layout) : display;
  const cfg = resolveDisplay(activeDisplay);
  const limit = cfg.items_limit === 'all' ? productCount : cfg.items_limit;
  const { data: products = [], isLoading } = useSectionProducts({ source: 'on_sale', limit });
  const countdown = useCountdown(showCountdown ? countdownEnd : undefined);

  if (!isLoading && products.length === 0) return null;

  return (
    <section className="py-12 md:py-20 bg-paper">
      <Container>
        <div className="mb-8 flex flex-wrap items-end justify-between gap-4 md:mb-10">
          <div>
            <Eyebrow>Sale</Eyebrow>
            {title && <h2 className="mt-2 font-serif text-2xl font-light text-ink md:mt-3 md:text-4xl">{title}</h2>}
            {subtitle && <p className="mt-1.5 text-sm text-stone md:mt-2">{subtitle}</p>}
          </div>
          {showCountdown && countdown && (
            <div className="flex items-center gap-2 md:gap-3">
              {[{ v: countdown.d, l: 'd' }, { v: countdown.h, l: 'h' }, { v: countdown.m, l: 'm' }, { v: countdown.s, l: 's' }].map(({ v, l }) => (
                <div key={l} className="flex flex-col items-center bg-ink text-cream px-2.5 py-1.5 rounded min-w-[38px] md:min-w-[44px] md:px-3 md:py-2">
                  <span className="text-lg font-semibold tabular-nums leading-none md:text-xl">{String(v).padStart(2, '0')}</span>
                  <span className="text-[9px] uppercase tracking-widest opacity-60 mt-0.5 md:mt-1 md:text-[10px]">{l}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        {isLoading ? (
          <div className="grid grid-cols-2 gap-5 md:grid-cols-4">
            {[0, 1, 2, 3].map((i) => <div key={i} className="aspect-[4/5] animate-pulse bg-stone-light/50" />)}
          </div>
        ) : (
          <SectionLayout
            items={products}
            display={activeDisplay}
            renderItem={(p) => <ProductCard key={p.id} product={p} onQuickView={onQuickView} display={cardDisplayProps(activeDisplay)} />}
          />
        )}
      </Container>
    </section>
  );
}

// ─── Dynamic section: Custom Product List ─────────────────────────────────────

function CustomProductListSection({
  sectionId: _sectionId, title, subtitle, productIds, layout, onQuickView,
}: {
  sectionId: number; title: string; subtitle?: string; productIds: number[];
  layout: string; onQuickView: (p: CatalogProduct) => void;
}) {
  const { data: products = [], isLoading } = useSectionProducts({ product_ids: productIds, limit: productIds.length || 12 });

  if (!isLoading && products.length === 0) return null;

  return (
    <section className="py-12 md:py-20">
      <Container>
        {(title || subtitle) && (
          <div className="mb-8 md:mb-10">
            {title && <h2 className="font-serif text-2xl font-light text-ink md:text-4xl">{title}</h2>}
            {subtitle && <p className="mt-1.5 text-sm text-stone md:mt-2">{subtitle}</p>}
          </div>
        )}
        {isLoading ? (
          <div className="grid grid-cols-2 gap-x-3 gap-y-8 md:grid-cols-4 md:gap-x-6 md:gap-y-10">
            {Array.from({ length: 4 }).map((_, i) => <div key={i} className="aspect-[4/5] animate-pulse bg-stone-light/50" />)}
          </div>
        ) : layout === 'list' ? (
          <div className="divide-y divide-stone-light border-y border-stone-light">
            {products.map(p => (
              <div key={p.id} className="flex gap-3 py-3.5 items-center md:gap-4 md:py-4">
                <div className="h-14 w-14 shrink-0 overflow-hidden rounded bg-stone-light/30 md:h-16 md:w-16">
                  {p.images?.[0]?.path && <img src={p.images[0].path.startsWith('http') ? p.images[0].path : `${API_URL}${p.images[0].path}`} alt={p.name} className="h-full w-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0">
                  <Link to={`/products/${p.slug}`} className="text-sm font-medium text-ink hover:text-clay line-clamp-1">{p.name}</Link>
                  <p className="mt-0.5 text-sm text-stone">${Number(p.base_price).toFixed(2)}</p>
                </div>
                <button type="button" onClick={() => onQuickView(p)}
                  className="shrink-0 border border-stone px-2.5 py-1.5 text-[10px] font-medium text-stone hover:border-ink hover:text-ink transition-colors md:px-3 md:text-[11px]">
                  Quick view
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-x-3 gap-y-8 md:grid-cols-4 md:gap-x-6 md:gap-y-10">
            {products.map(p => <ProductCard key={p.id} product={p} onQuickView={onQuickView} />)}
          </div>
        )}
      </Container>
    </section>
  );
}

// ─── Dynamic section: Recently Viewed ─────────────────────────────────────────

function RecentlyViewedSection({
  sectionId: _sectionId, title, maxCount, display, layout, onQuickView,
}: {
  sectionId: number; title: string; maxCount: number; display?: SectionDisplayConfig; layout?: LayoutConfig;
  onQuickView: (p: CatalogProduct) => void;
}) {
  const activeDisplay = layout ? layoutToDisplay(layout) : display;
  const effectiveMax = maxCount;
  const [ids, setIds] = useState<number[]>([]);
  useEffect(() => {
    setIds(getRecentlyViewedIds(effectiveMax));
  }, [effectiveMax]);

  const { data: products = [], isLoading } = useSectionProducts({ product_ids: ids, limit: effectiveMax });

  if (!isLoading && (ids.length === 0 || products.length === 0)) return null;

  return (
    <section className="py-12 md:py-20 bg-paper">
      <Container>
        <div className="mb-8 md:mb-10">
          <Eyebrow>Your History</Eyebrow>
          <h2 className="mt-2 font-serif text-2xl font-light text-ink md:mt-3 md:text-4xl">{title}</h2>
        </div>
        {isLoading ? (
          <div className="grid grid-cols-2 gap-5 md:grid-cols-4">
            {[0, 1, 2, 3].map((i) => <div key={i} className="aspect-[4/5] animate-pulse bg-stone-light/50" />)}
          </div>
        ) : (
          <SectionLayout
            items={products}
            display={activeDisplay}
            renderItem={(p) => <ProductCard key={p.id} product={p} onQuickView={onQuickView} display={cardDisplayProps(activeDisplay)} />}
          />
        )}
      </Container>
    </section>
  );
}

// ─── Dynamic section: Brand Showcase ──────────────────────────────────────────

function BrandShowcaseSection({
  sectionId: _sectionId, title, subtitle, productCount, showShopAll, display, layout, onQuickView,
}: {
  sectionId: number; title: string; subtitle?: string; productCount: number;
  showShopAll: boolean; display?: SectionDisplayConfig; layout?: LayoutConfig;
  onQuickView: (p: CatalogProduct) => void;
}) {
  const activeDisplay = layout ? layoutToDisplay(layout) : display;
  const effectiveShowShopAll = showShopAll;
  const cfg = resolveDisplay(activeDisplay);
  const limit = cfg.items_limit === 'all' ? productCount : cfg.items_limit;
  const { data: products = [], isLoading } = useSectionProducts({ source: 'new_arrivals', limit });

  if (!isLoading && products.length === 0) return null;

  return (
    <section className="py-12 md:py-20">
      <Container>
        <div className="mb-8 flex items-end justify-between md:mb-10">
          <div>
            {title && <h2 className="font-serif text-2xl font-light text-ink md:text-4xl">{title}</h2>}
            {subtitle && <p className="mt-1.5 text-sm text-stone md:mt-2">{subtitle}</p>}
          </div>
          {effectiveShowShopAll && (
            <Link to="/shop"
              className="hidden shrink-0 border border-ink px-5 py-2.5 text-[12px] font-medium uppercase tracking-[0.1em] text-ink transition-colors hover:bg-ink hover:text-cream md:block">
              Shop All
            </Link>
          )}
        </div>
        {isLoading ? (
          <div className="grid grid-cols-2 gap-5 md:grid-cols-4">
            {[0, 1, 2, 3].map((i) => <div key={i} className="aspect-[4/5] animate-pulse bg-stone-light/50" />)}
          </div>
        ) : (
          <SectionLayout
            items={products}
            display={activeDisplay}
            renderItem={(p) => <ProductCard key={p.id} product={p} onQuickView={onQuickView} display={cardDisplayProps(activeDisplay)} />}
          />
        )}
        {effectiveShowShopAll && (
          <div className="mt-8 text-center md:hidden">
            <Link to="/shop"
              className="inline-block border border-ink px-8 py-3 text-[12px] font-medium uppercase tracking-[0.1em] text-ink transition-colors hover:bg-ink hover:text-cream">
              Shop All
            </Link>
          </div>
        )}
      </Container>
    </section>
  );
}
